import { z } from "zod"

import { entityIdSchema } from "./identifiers.js"

export const communicationChannelV1Schema = z.enum(["email", "sms", "in_app"])
export const communicationProviderV1Schema = z.enum([
  "fake",
  "in_app",
  "email",
  "quo",
  "twilio",
])

export const communicationTemplatePublishV1Schema = z.object({
  templateId: entityIdSchema,
  templateCode: z.string().regex(/^[a-z][a-z0-9_]{2,63}$/),
  channel: communicationChannelV1Schema,
  locale: z.string().regex(/^[a-z]{2}(?:-[A-Z]{2})?$/),
  templateVersion: z.number().int().positive(),
  subjectTemplate: z.string().min(1).max(200).nullable(),
  bodyTemplate: z.string().min(1).max(4_000),
}).strict().superRefine((value, context) => {
  if (value.channel === "email" && value.subjectTemplate === null) {
    context.addIssue({ code: "custom", message: "Email subject required." })
  }
})

export const communicationPreferenceSetV1Schema = z.object({
  preferenceId: entityIdSchema,
  contactId: entityIdSchema,
  channel: communicationChannelV1Schema,
  state: z.enum(["opted_in", "opted_out"]),
  consentVersion: z.string().min(1).max(64).nullable(),
  consentedAt: z.string().datetime({ offset: true }).nullable(),
}).strict().superRefine((value, context) => {
  if (
    value.state === "opted_in" &&
    (value.consentVersion === null || value.consentedAt === null)
  ) context.addIssue({ code: "custom", message: "Consent evidence required." })
})

const templateVariablesV1Schema = z.record(
  z.string().regex(/^[a-z][a-zA-Z0-9]{0,31}$/),
  z.string().max(256),
).refine((value) => Object.keys(value).length <= 20)

export const communicationSendV1Schema = z.object({
  deliveryId: entityIdSchema,
  logicalMessageId: entityIdSchema,
  contactId: entityIdSchema,
  templateId: entityIdSchema,
  templateVersion: z.number().int().positive(),
  channel: communicationChannelV1Schema,
  providerCode: communicationProviderV1Schema,
  relatedEntityType: z.string().regex(/^[a-z][a-z0-9_]{2,63}$/),
  relatedEntityId: entityIdSchema,
  variables: templateVariablesV1Schema,
}).strict()

export const communicationCrossProviderResendV1Schema = z.object({
  deliveryId: entityIdSchema,
  originalDeliveryId: entityIdSchema,
  targetProviderCode: z.enum(["quo", "twilio"]),
  duplicateRiskAcknowledged: z.literal(true),
  reasonCode: z.enum(["uncertain_delivery", "provider_exhausted"]),
  variables: templateVariablesV1Schema,
}).strict()

export const communicationProviderWebhookResultV1Schema = z.object({
  resultVersion: z.literal(1),
  status: z.enum(["processed", "duplicate", "ignored"]),
  eventRef: z.string().regex(/^[0-9a-f]{16}$/),
}).strict()

export type CommunicationSendV1 = z.infer<typeof communicationSendV1Schema>
export type CommunicationProviderWebhookResultV1 = z.infer<
  typeof communicationProviderWebhookResultV1Schema
>
